import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Inbox, Plus, Search, Mail, MessageSquare, Phone, Globe,
  RefreshCw, Send, Eye, Archive, Clock, User, Hash
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNEL_CONFIG = {
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  whatsapp: { label: 'WhatsApp', icon: Phone, color: 'bg-emerald-100 text-emerald-700' },
  chat: { label: 'Chat', icon: Globe, color: 'bg-purple-100 text-purple-700' },
  phone: { label: 'Phone', icon: Phone, color: 'bg-amber-100 text-amber-700' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-500' },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function OmnichannelInboxPage() {
  const [conversations, setConversations] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterChannel, setFilterChannel] = useState('all');
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ contact_name: '', contact_email: '', channel: 'email', subject: '', initial_message: '' });

  const fetchData = useCallback(async () => {
    try {
      const [convos, dash] = await Promise.all([
        axios.get(`${API}/api/inbox/conversations`),
        axios.get(`${API}/api/inbox/dashboard`),
      ]);
      setConversations(convos.data.conversations || []);
      setDashboard(dash.data);
    } catch { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openConvo = async (convo) => {
    setActiveConvo(convo);
    try {
      const { data } = await axios.get(`${API}/api/inbox/conversations/${convo.id}/messages`);
      setMessages(data.messages || []);
    } catch { toast.error('Failed to load messages'); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo) return;
    try {
      await axios.post(`${API}/api/inbox/conversations/${activeConvo.id}/messages`, { content: newMessage, channel: activeConvo.channel });
      setNewMessage('');
      openConvo(activeConvo);
      fetchData();
    } catch { toast.error('Failed to send'); }
  };

  const updateStatus = async (cid, status) => {
    try {
      await axios.put(`${API}/api/inbox/conversations/${cid}/status`, { status });
      toast.success(`Marked as ${status}`);
      fetchData();
      if (activeConvo?.id === cid) setActiveConvo(prev => ({ ...prev, status }));
    } catch { toast.error('Failed'); }
  };

  const handleCreate = async () => {
    if (!form.contact_name.trim()) { toast.error('Contact name required'); return; }
    try {
      await axios.post(`${API}/api/inbox/conversations`, form);
      toast.success('Conversation created!');
      setShowCreate(false);
      setForm({ contact_name: '', contact_email: '', channel: 'email', subject: '', initial_message: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filtered = conversations.filter(c => {
    if (filterChannel !== 'all' && c.channel !== filterChannel) return false;
    if (search && !c.contact_name?.toLowerCase().includes(search.toLowerCase()) && !c.subject?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="omnichannel-inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Omnichannel Inbox<PageInfoTooltip pageKey="omnichannel-inbox" /></h1>
          <p className="text-gray-medium text-sm mt-1">Manage all customer conversations in one place</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-conversation-btn">
          <Plus className="h-4 w-4 mr-1" /> New Conversation
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Inbox} label="Total" value={dashboard.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Clock} label="Open" value={dashboard.open || 0} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Eye} label="Unread" value={dashboard.unread || 0} color="bg-red-50 text-red-600" />
        <StatCard icon={Archive} label="Resolved" value={dashboard.resolved || 0} color="bg-emerald-50 text-emerald-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: '500px' }}>
        {/* Conversation List */}
        <Card className="bg-white border-gray-border lg:col-span-1">
          <CardHeader className="pb-2">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search..." className="pl-9 h-8 text-sm" value={search} onChange={e => setSearch(e.target.value)} data-testid="inbox-search" />
              </div>
              <Select value={filterChannel} onValueChange={setFilterChannel}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  {Object.entries(CHANNEL_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[450px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-gray-medium text-sm">No conversations</div>
            ) : filtered.map(convo => {
              const chCfg = CHANNEL_CONFIG[convo.channel] || CHANNEL_CONFIG.email;
              const stCfg = STATUS_CONFIG[convo.status] || STATUS_CONFIG.open;
              return (
                <div key={convo.id} className={cn("flex items-start gap-2.5 px-3 py-2.5 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50", activeConvo?.id === convo.id && "bg-blue-50/50")} onClick={() => openConvo(convo)} data-testid={`convo-${convo.id}`}>
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5", chCfg.color)}>
                    <chCfg.icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-medium text-sm text-text-primary truncate">{convo.contact_name}</span>
                      {convo.unread_count > 0 && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-medium truncate">{convo.last_message || convo.subject || 'No messages'}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Badge className={cn("text-[9px] px-1 py-0 h-4", stCfg.color)}>{stCfg.label}</Badge>
                      <span className="text-[10px] text-gray-400">{convo.last_message_at ? new Date(convo.last_message_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Messages Panel */}
        <Card className="bg-white border-gray-border lg:col-span-2 flex flex-col">
          {activeConvo ? (
            <>
              <CardHeader className="pb-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-sm">{activeConvo.contact_name}</span>
                    <Badge className={cn("text-[10px]", (STATUS_CONFIG[activeConvo.status] || STATUS_CONFIG.open).color)}>{activeConvo.status}</Badge>
                  </div>
                  <div className="flex gap-1">
                    {activeConvo.status === 'open' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateStatus(activeConvo.id, 'resolved')}>Resolve</Button>}
                    {activeConvo.status === 'resolved' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateStatus(activeConvo.id, 'open')}>Reopen</Button>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[350px]">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-medium text-sm py-8">No messages in this conversation yet</div>
                ) : messages.map(msg => (
                  <div key={msg.id} className={cn("max-w-[80%] p-2.5 rounded-lg text-sm", msg.sender === 'agent' ? "ml-auto bg-emerald-50 text-emerald-900" : "bg-gray-100 text-text-primary")} data-testid={`msg-${msg.id}`}>
                    <p className="text-[10px] font-medium mb-0.5 opacity-60">{msg.sender_name || msg.sender}</p>
                    <p>{msg.content}</p>
                  </div>
                ))}
              </CardContent>
              <div className="p-3 border-t border-gray-100">
                <div className="flex gap-2">
                  <Input placeholder="Type a message..." value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} className="flex-1" data-testid="inbox-message-input" />
                  <Button onClick={sendMessage} disabled={!newMessage.trim()} data-testid="inbox-send-btn"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-medium">
              <div className="text-center">
                <Inbox className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a conversation to view messages</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Conversation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} data-testid="inbox-contact-name" /></div>
            <div><Label>Contact Email</Label><Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
            <div><Label>Channel</Label>
              <Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CHANNEL_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} /></div>
            <div><Label>Initial Message</Label><Textarea value={form.initial_message} onChange={e => setForm(f => ({ ...f, initial_message: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-conversation-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
